import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChatCircleText, EnvelopeSimple, ArrowCounterClockwise, FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import { renderMessage, clearMessageTemplateCache } from "../lib/messageTemplates";

const TOKEN_LABELS = {
  customer: "Customer first name", device: "Device", ticket: "Ticket no.", estimate: "Estimate",
  balance: "Balance due", invoice: "Invoice no.", total: "Total", paid: "Paid", amount: "Amount",
  link: "Link", review_link: "Review link", store: "Store name", store_phone: "Store phone", method: "Refund method",
};
const SAMPLE = {
  customer: "Mandeep", device: "Apple iPhone 14", ticket: "T-1023", estimate: "199.00", balance: "99.00",
  invoice: "S-001151", total: "199.00", paid: "100.00", amount: "99.00", link: "https://irepairpos.com/pay/…",
  review_link: "https://g.page/r/…", method: "Cash",
};

function TokenChips({ tokens, onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tokens.map((t) => (
        <button key={t} type="button" onClick={() => onInsert(`{${t}}`)} data-testid={`token-${t}`}
                className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-[#1E5FEB] hover:border-[#BFDBFE] hover:bg-[#F5F9FF] transition-colors">
          {TOKEN_LABELS[t] || t}
        </button>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, rows = 5, testid, fieldRef, onFocus }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {rows === 1 ? (
        <input ref={fieldRef} value={value} onFocus={onFocus} onChange={(e) => onChange(e.target.value)} data-testid={testid}
               className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 outline-none text-[13px]" />
      ) : (
        <textarea ref={fieldRef} value={value} rows={rows} onFocus={onFocus} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 outline-none text-[13px] leading-relaxed font-mono" />
      )}
    </label>
  );
}

function Preview({ icon: Icon, title, children, meta }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#FAFBFC] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
          <Icon size={13} weight="bold" className="text-[#1E5FEB]" /> {title}
        </div>
        {meta && <div className="text-[10.5px] text-slate-400 tabular-nums">{meta}</div>}
      </div>
      {children}
    </div>
  );
}

function Editor({ tpl, store, isAdmin, onSaved }) {
  const [draft, setDraft] = useState({ sms: tpl.sms, email_subject: tpl.email_subject, email_body: tpl.email_body });
  const [saving, setSaving] = useState(false);
  const focused = useRef("sms");
  const refs = { sms: useRef(null), email_subject: useRef(null), email_body: useRef(null) };
  useEffect(() => { setDraft({ sms: tpl.sms, email_subject: tpl.email_subject, email_body: tpl.email_body }); }, [tpl]);

  const ctx = useMemo(() => ({ ...SAMPLE, store: store?.name || "Your store", store_phone: store?.phone || "07 0000 0000" }), [store]);
  const dirty = ["sms", "email_subject", "email_body"].some((f) => (draft[f] || "") !== (tpl[f] || ""));
  const hasEmail = tpl.channels.includes("email");
  const smsText = renderMessage(draft.sms, ctx);
  const segments = Math.max(1, Math.ceil(smsText.length / 160));

  const insert = (token) => {
    const f = focused.current;
    const el = refs[f].current;
    const v = draft[f] || "";
    const pos = el ? el.selectionStart ?? v.length : v.length;
    setDraft((d) => ({ ...d, [f]: v.slice(0, pos) + token + v.slice(pos) }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(pos + token.length, pos + token.length);
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = hasEmail ? draft : { sms: draft.sms };
      await api.put(`/message-templates/${tpl.key}`, payload);
      clearMessageTemplateCache();
      toast.success(`"${tpl.label}" saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try {
      await api.delete(`/message-templates/${tpl.key}`);
      clearMessageTemplateCache();
      toast.success("Back to the default wording");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6" data-testid={`template-editor-${tpl.key}`}>
      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Insert detail (tap to add where the cursor is)</div>
          <TokenChips tokens={tpl.tokens} onInsert={insert} />
        </div>
        <Field label="SMS" value={draft.sms} rows={7} testid="tpl-sms" fieldRef={refs.sms}
               onFocus={() => { focused.current = "sms"; }} onChange={(v) => setDraft((d) => ({ ...d, sms: v }))} />
        {hasEmail && (
          <>
            <Field label="Email subject" value={draft.email_subject} rows={1} testid="tpl-email-subject" fieldRef={refs.email_subject}
                   onFocus={() => { focused.current = "email_subject"; }} onChange={(v) => setDraft((d) => ({ ...d, email_subject: v }))} />
            <Field label="Email message" value={draft.email_body} rows={7} testid="tpl-email-body" fieldRef={refs.email_body}
                   onFocus={() => { focused.current = "email_body"; }} onChange={(v) => setDraft((d) => ({ ...d, email_body: v }))} />
          </>
        )}
        {isAdmin ? (
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={!dirty || saving} data-testid="tpl-save"
                    className="h-10 px-4 rounded-xl bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] disabled:opacity-40 flex items-center gap-2 shadow-[0_6px_16px_-8px_rgba(30,95,235,0.8)]">
              {saving ? <CircleNotch size={15} className="animate-spin" /> : <FloppyDisk size={15} weight="bold" />} Save
            </button>
            {tpl.customized && (
              <button onClick={reset} data-testid="tpl-reset"
                      className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-[13px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                <ArrowCounterClockwise size={15} weight="bold" /> Reset to default
              </button>
            )}
          </div>
        ) : (
          <div className="text-[12px] text-slate-500">Only admins can change message wording.</div>
        )}
      </div>
      <div className="space-y-3">
        <Preview icon={ChatCircleText} title="SMS preview" meta={`${smsText.length} chars · ${segments} SMS`}>
          <div className="max-w-[300px] rounded-2xl rounded-bl-md bg-white border border-slate-200 px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap" data-testid="tpl-sms-preview">
            {smsText}
          </div>
        </Preview>
        {hasEmail && (
          <Preview icon={EnvelopeSimple} title="Email preview">
            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-[13px] font-bold text-slate-900 mb-3" data-testid="tpl-email-subject-preview">{renderMessage(draft.email_subject, ctx)}</div>
              <div className="text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-wrap">{renderMessage(draft.email_body, ctx)}</div>
            </div>
          </Preview>
        )}
      </div>
    </div>
  );
}

export default function MessageTemplatesTab() {
  const { currentStore } = useStore();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("ticket_booked");

  const load = () => api.get("/message-templates").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [currentStore?.id]);

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading messages…</div>;
  const tpl = data.templates.find((t) => t.key === active) || data.templates[0];

  return (
    <div className="p-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8" data-testid="message-templates-tab">
      <nav className="space-y-5">
        {data.groups.map((g) => (
          <div key={g}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5 px-2">{g}</div>
            {data.templates.filter((t) => t.group === g).map((t) => (
              <button key={t.key} onClick={() => setActive(t.key)} data-testid={`tpl-nav-${t.key}`}
                      className={`w-full text-left px-3 py-2 rounded-xl text-[13px] font-semibold flex items-center justify-between gap-2 transition-colors ${
                        t.key === tpl.key ? "bg-[#EFF6FF] text-[#1E5FEB]" : "text-slate-700 hover:bg-slate-50"
                      }`}>
                <span className="truncate">{t.label}</span>
                <span className="flex items-center gap-1 shrink-0 text-slate-400">
                  <ChatCircleText size={13} weight="bold" />
                  {t.channels.includes("email") && <EnvelopeSimple size={13} weight="bold" />}
                  {t.customized && <span className="w-1.5 h-1.5 rounded-full bg-[#1E5FEB]" title="Edited" />}
                </span>
              </button>
            ))}
          </div>
        ))}
      </nav>
      <section className="min-w-0">
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">{tpl.group}</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">{tpl.label}</h2>
          <p className="text-[12.5px] text-slate-500 mt-1">Wording used for {currentStore?.name || "this store"}. Details in blue are filled in automatically.</p>
        </div>
        <Editor key={tpl.key} tpl={tpl} store={currentStore} isAdmin={user?.role === "admin"} onSaved={load} />
      </section>
    </div>
  );
}
